// ===========================================
// SCHEMA: MarcarPresentado
// ===========================================
// Schema Zod para el formulario de marcar vencimiento como presentado

import { z } from 'zod'
import { CATEGORIAS_MONOTRIBUTO } from '../mensajeRecategorizacion'

export const marcarPresentadoSchema = z
  .object({
    fecha_presentacion: z.string().min(1, 'La fecha de presentación es requerida'),
    numero_presentacion: z.string().nullable().optional(),
    // El rango se valida abajo, porque depende de saldo_tipo.
    monto_determinado: z.number().nullable().optional(),
    saldo_tipo: z.enum(['a_pagar', 'a_favor', 'sin_movimiento']).nullable().optional(),
    forma_pago: z.enum(['VEP', 'debito_automatico', 'efectivo']).nullable().optional(),
    observaciones: z.string().nullable().optional(),
  })
  // Si el saldo es "a pagar" o "a favor", el monto es obligatorio (> 0).
  // "Sin movimiento" se registra en $0: no exige monto ni valida el que haya quedado
  // cargado, porque el input está oculto y el error no se vería.
  .superRefine((val, ctx) => {
    if (val.saldo_tipo === 'sin_movimiento') return
    const monto = val.monto_determinado
    const issue = (message: string) =>
      ctx.addIssue({ code: 'custom', path: ['monto_determinado'], message })
    if ((val.saldo_tipo === 'a_pagar' || val.saldo_tipo === 'a_favor') && !(monto != null && monto > 0)) {
      issue('Ingresá el monto')
    } else if (monto != null && monto < -99999999) {
      issue('El monto no puede ser menor a -99.999.999')
    } else if (monto != null && monto > 99999999) {
      issue('El monto no puede ser mayor a 99.999.999')
    }
  })

export type MarcarPresentadoFormValues = z.infer<typeof marcarPresentadoSchema>

// Schema for manual (non-fiscal) vencimientos — simplified form
export const marcarPresentadoManualSchema = z.object({
  fecha_presentacion: z.string().min(1, 'La fecha de presentación es requerida'),
  mensaje_libre: z.string().min(1, 'El mensaje para el cliente es requerido'),
  observaciones: z.string().nullable().optional(),
})

export type MarcarPresentadoManualFormValues = z.infer<typeof marcarPresentadoManualSchema>

// ORR-238: recategorización semestral. Es un vencimiento manual (recurrente), pero
// en vez del mensaje libre pide los datos con los que se arma el mensaje al cliente.
//
// Schema aparte y NO una variante del manual: aquel exige `mensaje_libre` con
// `min(1)`, y acá el textarea no se renderiza — reusarlo bloquearía el submit con
// un error sobre un campo que el usuario no puede ver.
export const marcarPresentadoRecategorizacionSchema = z.object({
  fecha_presentacion: z.string().min(1, 'La fecha de presentación es requerida'),
  categoria_monotributo: z.enum(CATEGORIAS_MONOTRIBUTO, {
    message: 'Elegí la nueva categoría',
  }),
  regimen_recategorizacion: z.enum(['monotributo', 'monotributo_unificado'], {
    message: 'Elegí el régimen',
  }),
  // El monto va al mensaje del cliente y a `monto_determinado`, así que es obligatorio.
  monto_determinado: z
    .number({ message: 'Ingresá el monto de la cuota' })
    .positive('El monto debe ser mayor a 0')
    .max(99999999, 'El monto no puede ser mayor a 99.999.999'),
  observaciones: z.string().nullable().optional(),
})

export type MarcarPresentadoRecategorizacionFormValues = z.infer<
  typeof marcarPresentadoRecategorizacionSchema
>
